/**
 * Execution Lifecycle Classifiers — read-only derivation.
 * ─────────────────────────────────────────────────────────────────
 * Companion pattern to `postScheduleBlockClassifier` for the rest of
 * the /execution domain.
 *
 * Every classifier here answers the same three operator questions
 * consistently across pages:
 *   1. What lifecycle bucket does this row belong to?
 *      → `*Lifecycle` helpers return `{ bucket, label, detail }`.
 *   2. Why is it here / is anything unusual?
 *      → `*Anomaly` helpers return `{ tag, label, detail } | null`.
 *
 * Pure. No mutations. No new truth. No I/O. No copy beyond what is
 * needed for the UI to render the verdict.
 *
 * Anchor: /execution honesty pass (Install Today → siblings).
 */
import type { Callback } from "@/data/contractor/networkExecution/callback";
import type { HoldbackState } from "@/data/contractor/networkExecution/holdback/deriveState";

/* ───────────────────────── shared types ───────────────────────── */

export interface LifecycleVerdict<Bucket extends string> {
  bucket: Bucket;
  label: string;
  detail: string;
}

export interface AnomalyVerdict<Tag extends string> {
  tag: Tag;
  label: string;
  detail: string;
  /** UI hint for badge tone. */
  tone: "warning" | "destructive" | "muted";
}

const DAY_MS = 86_400_000;

/* ═══════════════════════════ CALLBACKS ═══════════════════════════ */

export type CallbackLifecycleBucket = "fresh" | "aging" | "stale";

/**
 * Lifecycle bucket for a QA callback. Time-since-filed only — the
 * callback model is append-only so "resolved" is not a status we can
 * read. Buckets exist to make the list scannable, not to imply SLA.
 */
export function classifyCallbackLifecycle(
  callback: Pick<Callback, "filedAt">,
  nowMs: number = Date.now(),
): LifecycleVerdict<CallbackLifecycleBucket> {
  const filedMs = Date.parse(callback.filedAt);
  const age = Number.isFinite(filedMs) ? nowMs - filedMs : 0;

  if (age <= DAY_MS) {
    return {
      bucket: "fresh",
      label: "Fresh (≤24h)",
      detail: "Filed in the last 24 hours — still in triage window.",
    };
  }
  if (age <= 7 * DAY_MS) {
    return {
      bucket: "aging",
      label: "Aging (1–7d)",
      detail: "Filed 1–7 days ago — should be in active review.",
    };
  }
  return {
    bucket: "stale",
    label: "Stale (>7d)",
    detail:
      "Filed over a week ago with no resolution recorded on the canonical sink. Worth confirming the disposition exists somewhere.",
  };
}

export type CallbackAnomalyTag =
  | "owner_attributed"
  | "unassigned_installer"
  | "high_severity_system";

/**
 * Why is this callback worth a second look?
 * Returns null when nothing is unusual.
 *
 * Order matters — we surface the most operationally significant tag
 * first, since rows show one tag.
 */
export function classifyCallbackAnomaly(
  callback: Pick<
    Callback,
    "attributedTo" | "installerContractorIdAtCallback" | "severity" | "source"
  >,
): AnomalyVerdict<CallbackAnomalyTag> | null {
  // Owner-attributed is the most important distinction: per the
  // locked Phase 1b formula, owner-attributed callbacks are
  // EXCLUDED from executor scorecards. Reviewers should know that
  // before they read the row as evidence against an installer.
  if (callback.attributedTo === "owner") {
    return {
      tag: "owner_attributed",
      label: "Owner-attributed",
      detail:
        "Attributed to the owner contractor — excluded from executor scorecards by canonical rule.",
      tone: "muted",
    };
  }

  if (callback.installerContractorIdAtCallback === null) {
    return {
      tag: "unassigned_installer",
      label: "No installer snapshot",
      detail:
        "Filed with no installer assigned at the time. Cannot attribute to an executor — review the dispatch trail.",
      tone: "warning",
    };
  }

  if (callback.severity === "high" && callback.source === "system") {
    return {
      tag: "high_severity_system",
      label: "High · system-detected",
      detail:
        "Auto-detected high-severity signal — confirm a human has acknowledged before it ages out of triage.",
      tone: "destructive",
    };
  }

  return null;
}

/* ═══════════════════════════ HOLDBACKS ═══════════════════════════ */

export type HoldbackLifecycleBucket = "active" | "paused" | "resolved";

/**
 * Lifecycle bucket for a derived holdback. Folds the four canonical
 * statuses into three operator-facing buckets:
 *   - active   → still holding funds, not paused
 *   - paused   → held but paused by a dispute (release blocked)
 *   - resolved → released or fully reversed (terminal for operators)
 */
export function classifyHoldbackLifecycle(
  state: Pick<HoldbackState, "status">,
): LifecycleVerdict<HoldbackLifecycleBucket> {
  switch (state.status) {
    case "active":
      return {
        bucket: "active",
        label: "Active hold",
        detail: "Funds currently held. No dispute pause and no release recorded.",
      };
    case "paused":
      return {
        bucket: "paused",
        label: "Paused (dispute)",
        detail: "Release is paused while a dispute is open against the hold or its source callback.",
      };
    case "released":
      return {
        bucket: "resolved",
        label: "Released",
        detail: "Hold has been released (net held is zero). Terminal for operator review.",
      };
    case "reversed":
      return {
        bucket: "resolved",
        label: "Reversed",
        detail: "Hold was reversed via append-only reverse entry. Terminal for operator review.",
      };
  }
}

export type HoldbackAnomalyTag =
  | "reversed_override"
  | "partial_release"
  | "long_paused";

/**
 * Why is this holdback worth a second look?
 * Returns null when nothing is unusual.
 */
export function classifyHoldbackAnomaly(
  state: HoldbackState,
  nowMs: number = Date.now(),
): AnomalyVerdict<HoldbackAnomalyTag> | null {
  // Reverse entries are administrative undo of a hold — always worth
  // surfacing as anomalous, since they bypass the normal
  // release path.
  const hasReverse = state.entries.some((e) => e.action === "reverse");
  if (hasReverse) {
    return {
      tag: "reversed_override",
      label: "Reversed",
      detail:
        "An admin reverse entry exists for this holdback — append-only undo of a prior create or release. Confirm intent.",
      tone: "destructive",
    };
  }

  // Multiple releases on an active/paused hold suggests it has been
  // partially drawn down — operators should know the residual is not
  // the original gross amount.
  const releaseCount = state.entries.filter((e) => e.action === "release").length;
  const createCount = state.entries.filter((e) => e.action === "create").length;
  if (state.status !== "released" && releaseCount > 0 && createCount > 0) {
    return {
      tag: "partial_release",
      label: "Partially released",
      detail:
        "One or more partial releases have been applied. The shown net is the residual, not the original gross hold.",
      tone: "warning",
    };
  }

  if (state.status === "paused") {
    const pauseEntry = [...state.entries].reverse().find((e) => e.action === "pause");
    if (pauseEntry) {
      const pausedMs = Date.parse(pauseEntry.createdAt);
      if (Number.isFinite(pausedMs) && nowMs - pausedMs > 14 * DAY_MS) {
        return {
          tag: "long_paused",
          label: "Paused >14d",
          detail:
            "Dispute pause has been in place for over two weeks. Confirm the dispute is still active.",
          tone: "warning",
        };
      }
    }
  }

  return null;
}

/* ═══════════════════════ display ordering ═══════════════════════ */

export const CALLBACK_BUCKET_ORDER: CallbackLifecycleBucket[] = ["fresh", "aging", "stale"];
export const HOLDBACK_BUCKET_ORDER: HoldbackLifecycleBucket[] = ["active", "paused", "resolved"];
